import React, { useEffect, useState } from "react";
import { FileMetadata, PathTypeSchema } from "@domain/schemas/BI";
import { State } from "@progress/kendo-data-query";
import { useInjection } from "@presentation/components/dependencyInjection";
import { StorageConfigurationService } from "@application/useCases/workspace/StorageConfigurationService";

export type SelectionVisualState = "none" | "partial" | "all";

export type ExplorerItem = {
  id: string;
  fileName: string;
  isFolder: boolean;
  sizeInBytes: number;
  sourcePath?: string | null;
  /**
   * `id` alone collides between two configured roots that happen to share a relative path - used for
   * the Grid's `dataItemKey` and for `loadingFolderId`/selection keying so a click or selection on one
   * colliding row can never be confused with its sibling.
   */
  rowKey: string;
  /**
   * Count of files directly/indirectly under a folder, when the facet's listing knows it up front (Raw
   * only) - lets `folderSelectionStates` report "all" precisely instead of only via an explicit
   * select-all-descendants checkbox click. `null`/absent for facets that don't track it.
   */
  fileCount?: number | null;
};

export type ExplorerSelectedState = Record<string, FileMetadata & { isFolder: boolean }>;

export type ExplorerBreadcrumbItem = { id: string; text: string; disabled: boolean; sourcePath?: string | null };

export const buildRowKey = (id: string, sourcePath?: string | null) => (sourcePath ? `${sourcePath}::${id}` : id);

/**
Synthetic, purely client-side "folder" entry representing one of a project's 2+ configured roots for
a facet - rendered at Home instead of merging every root's contents together (the source of same-name
collisions across roots). Navigating into it scopes all further listing to that root's `sourcePath`,
exactly like any other folder.
*/
export const buildRootChoiceItem = (root: string): ExplorerItem => ({
  id: "",
  rowKey: buildRowKey("", root),
  fileName: root,
  isFolder: true,
  sizeInBytes: 0,
  sourcePath: root,
});

export type ShiftSelectionEvent = React.SyntheticEvent<Element> & {
  shiftKey?: boolean;
  nativeEvent: Event & { shiftKey?: boolean };
};

export const hasShiftKey = (event: ShiftSelectionEvent) => (typeof event.shiftKey === "boolean" ? event.shiftKey : Boolean(event.nativeEvent?.shiftKey));

export const normalizePathValue = (value?: string | null) => (value ?? "").replaceAll("\\", "/").replace(/^\/+/, "").replace(/\/+$/, "");

export const normalizeFolderKey = (folder: string) =>
  !folder || folder === "home"
    ? ""
    : folder
        .trim()
        .replaceAll("\\", "/")
        .replace(/^\/+/, "")
        .replaceAll(/\/{2,}/g, "/") || "";

/**
 * Store slice this hook reads/writes - the caller owns the actual Zustand store (so other components
 * outside the grid, e.g. a form-block summary or job-rerun repopulation, can still read/write the same
 * state), the hook only needs it handed in under these common names.
 */
export interface ExplorerStoreSlice {
  items: ExplorerItem[];
  setItems: (items: ExplorerItem[]) => void;
  selectedState: ExplorerSelectedState;
  setSelectedState: (state: ExplorerSelectedState) => void;
  breadcrumbItems: ExplorerBreadcrumbItem[];
  setBreadcrumbItems: (items: ExplorerBreadcrumbItem[]) => void;
  resetSelectedGridData: () => void;
  lastProjectId: string | null;
  setLastProjectId: (projectId: string) => void;
  metaState: State;
  setMetaState: (state: State) => void;
}

export interface UseRemoteFolderExplorerOptions<TApiItem> {
  activeProjectId: string | null;
  store: ExplorerStoreSlice;
  /**
   * Which facet this explorer browses - used to resolve the project's configured roots up front, so
   * Home can offer them as entries to choose between instead of silently merging their contents
   * (the merge was the source of same-name collisions across roots, mitigated only by a hard-to-spot
   * badge). With 0 or 1 resolved root there is nothing to disambiguate, so Home lists directly as before.
   */
  pathType: PathTypeSchema;
  /**
   * "single" clears any other selection when a new file is checked, and never lets a folder be selected
   * (folders only make sense to select-all-descendants-of in "multi" mode).
   */
  selectionMode: "single" | "multi";
  fetchList: (folder: string, sourcePath: string | null, shouldForceRefresh: boolean) => Promise<{ list?: TApiItem[] | null }>;
  toExplorerItem: (apiItem: TApiItem) => {
    location?: string | null;
    fileName?: string | null;
    sizeInBytes?: number | null;
    sourcePath?: string | null;
    fileCount?: number | null;
    /**
     * Overrides the default "blank fileName means folder" inference for the row's FINAL `isFolder` -
     * `id`/`fileName` are still derived the normal (folder-shaped) way. Raw uses this to treat `.d`
     * folders as plain files (no recursion, no folder icon) while still deriving their name from the
     * folder-shaped `location`.
     */
    isFolderOverride?: boolean;
  };
  onSelectionChange: (selected: { path: string; sizeInBytes: number; sourcePath?: string | null }[]) => void;
  /**
   * Files (not folders) whose name doesn't end with one of these are excluded from the listing - Raw
   * only, not applied to recursive "select whole folder" fetches (matches its pre-migration behavior).
   */
  allowedExtensions?: string[];
  /**
   * Extra scope predicate beyond the normal facet listing - Raw only, used when the explorer is embedded
   * with a caller-narrowed scope (e.g. one analysis step's own sub-path). Governs what's fetched, stored,
   * and selectable (NOT the same as the regex view-filter, which only affects what's currently rendered).
   * Applied to both the main listing and recursive "select whole folder" fetches.
   */
  isItemVisibleInScope?: (itemId: string, isFolder: boolean) => boolean;
  /**
   * When true, unchecking anything is a no-op - Raw's DIA-analysis-rerun lock (additions only, removals
   * disabled) for the specific job-setup step this explorer is embedded in.
   */
  preventDeselection?: boolean;
}

/**
 * Shared folder-navigation + collision-safe selection core for Raw/Fasta/SoftwareSettings-style file
 * pickers. Handles: cached, in-flight-deduped fetching per (folder, sourcePath); breadcrumb navigation;
 * project-switch detection that survives the owning tab being unmounted (Kendo's `TabStripTab` unmounts
 * inactive tabs, so this is tracked in the caller's store, not a component-local ref); multi-select with
 * shift-range + select-all-visible + recursive folder selection, or single-select; and `rowKey`-based
 * identity throughout so two configured roots that happen to share a relative path never get confused
 * with each other (previously the cause of a stuck loading spinner and of selection bleeding between
 * colliding rows).
 */
export function useRemoteFolderExplorer<TApiItem>(options: UseRemoteFolderExplorerOptions<TApiItem>) {
  const { activeProjectId, store, pathType, selectionMode, fetchList, toExplorerItem, onSelectionChange, allowedExtensions, isItemVisibleInScope, preventDeselection } = options;
  const { items, setItems, selectedState, setSelectedState, breadcrumbItems, setBreadcrumbItems, resetSelectedGridData, lastProjectId, setLastProjectId, metaState, setMetaState } = store;

  const [loading, setLoading] = useState(false);
  const [loadingFolderId, setLoadingFolderId] = useState<string | null>(null);
  const selectionAnchorIdReference = React.useRef<string | null>(null);
  const selectionRevisionReference = React.useRef(0);
  const cacheRef = React.useRef<Map<string, { list?: TApiItem[] | null }>>(new Map());
  const inFlightRef = React.useRef<Map<string, Promise<{ list?: TApiItem[] | null }>>>(new Map());
  // Guards against an earlier, slower fetch (e.g. a stale candidate-fallback retry) overwriting a later
  // one's result when folders are clicked in quick succession.
  const latestFetchRequestIdRef = React.useRef(0);
  const storageConfigurationService = useInjection(StorageConfigurationService);
  // Mirrors `resolvedRoots` state for synchronous reads inside `fetchData` (avoids a stale closure -
  // `fetchData` must know the latest roots without waiting on a re-render).
  const resolvedRootsRef = React.useRef<string[] | null>(null);

  const loadResolvedRoots = React.useCallback(async (): Promise<string[]> => {
    if (!activeProjectId) {
      resolvedRootsRef.current = [];
      return [];
    }
    try {
      const effectiveConfig = await storageConfigurationService.getProjectEffectiveStorageConfigurationAsync(activeProjectId);
      const roots = [...new Set((effectiveConfig.paths?.[pathType] ?? []).map((entry) => entry.path?.trim()).filter((path): path is string => Boolean(path)))];
      resolvedRootsRef.current = roots;
      return roots;
    } catch (error) {
      console.error("Error resolving configured roots:", error);
      resolvedRootsRef.current = [];
      return [];
    }
  }, [activeProjectId, pathType, storageConfigurationService]);

  const getListWithCache = React.useCallback(
    (folder: string, sourcePath: string | null, shouldForceRefresh = false) => {
      const normalizedFolder = normalizeFolderKey(folder);
      const cacheKey = `${activeProjectId}::${normalizedFolder}::${sourcePath ?? ""}`;

      if (shouldForceRefresh) {
        cacheRef.current.delete(cacheKey);
        inFlightRef.current.delete(cacheKey);
      }

      const cached = cacheRef.current.get(cacheKey);
      if (cached) return Promise.resolve(cached);

      const inFlight = inFlightRef.current.get(cacheKey);
      if (inFlight) return inFlight;

      if (!activeProjectId) return Promise.resolve({ list: [] });

      const request = (async () => {
        try {
          const response = await fetchList(normalizedFolder, sourcePath, shouldForceRefresh);
          cacheRef.current.set(cacheKey, response);
          return response;
        } finally {
          inFlightRef.current.delete(cacheKey);
        }
      })();

      inFlightRef.current.set(cacheKey, request);
      return request;
    },
    [activeProjectId, fetchList],
  );

  const clearCache = () => {
    cacheRef.current.clear();
    inFlightRef.current.clear();
  };

  const toItem = React.useCallback(
    (apiItem: TApiItem): ExplorerItem => {
      const normalized = toExplorerItem(apiItem);
      const location = normalizePathValue(normalized.location);
      const fileName = String(normalized.fileName ?? "");
      // Structural shape only (API convention: blank fileName = folder row) - `id`/`fileName` always
      // derive from this, regardless of `isFolderOverride`.
      const isStructuralFolder = fileName.trim().length === 0;
      const id = isStructuralFolder ? location : [location, fileName].filter(Boolean).join("/");
      const name = isStructuralFolder ? (location.split("/").pop() ?? "") : fileName;
      return {
        id,
        rowKey: buildRowKey(id, normalized.sourcePath),
        fileName: name || fileName,
        isFolder: normalized.isFolderOverride ?? isStructuralFolder,
        sizeInBytes: normalized.sizeInBytes ?? 0,
        sourcePath: normalized.sourcePath ?? null,
        fileCount: normalized.fileCount ?? null,
      };
    },
    [toExplorerItem],
  );

  const fetchData = React.useCallback(
    async (folder: string, sourcePath: string | null = null, shouldForceRefresh = false) => {
      const requestId = ++latestFetchRequestIdRef.current;
      const isAmbiguousHome = normalizeFolderKey(folder) === "" && !sourcePath;
      if (isAmbiguousHome) {
        setLoading(true);
        const roots = resolvedRootsRef.current ?? (await loadResolvedRoots());
        if (requestId !== latestFetchRequestIdRef.current) return;
        if (roots.length >= 2) {
          setItems(roots.map((root) => buildRootChoiceItem(root)));
          setLoading(false);
          setLoadingFolderId(null);
          return;
        }
      }

      setLoading(true);
      try {
        const response = await getListWithCache(folder, sourcePath, shouldForceRefresh);
        if (requestId !== latestFetchRequestIdRef.current) return;
        const mappedItems = (response.list ?? []).map((apiItem) => toItem(apiItem));
        const extensionFiltered = allowedExtensions ? mappedItems.filter((item) => item.isFolder || allowedExtensions.some((extension) => item.fileName.endsWith(extension))) : mappedItems;
        const scopedItems = isItemVisibleInScope ? extensionFiltered.filter((item) => isItemVisibleInScope(item.id, item.isFolder)) : extensionFiltered;
        setItems(scopedItems);
      } catch (error) {
        if (requestId !== latestFetchRequestIdRef.current) return;
        console.error("Error fetching folder listing:", error);
      } finally {
        if (requestId === latestFetchRequestIdRef.current) {
          setLoading(false);
          setLoadingFolderId(null);
        }
      }
    },
    [allowedExtensions, getListWithCache, isItemVisibleInScope, loadResolvedRoots, setItems, toItem],
  );

  useEffect(() => {
    if (!activeProjectId || lastProjectId === activeProjectId) return;
    const isProjectSwitch = lastProjectId !== null;
    setLastProjectId(activeProjectId);
    // Roots are resolved per-project - a stale array from the previous project must never leak into
    // the new one's ambiguous-Home check in `fetchData`.
    resolvedRootsRef.current = null;

    if (isProjectSwitch) {
      // A folder path / selection from the previous project means nothing in this one.
      resetSelectedGridData();
      setBreadcrumbItems([{ id: "home", text: breadcrumbItems[0]?.text ?? "Root", disabled: false }]);
      void fetchData("home");
      return;
    }

    const lastBreadcrumbItem = breadcrumbItems.at(-1);
    void fetchData(lastBreadcrumbItem?.id ?? "home", lastBreadcrumbItem?.sourcePath ?? null);
  }, [activeProjectId]);

  const folderSelectionStates = React.useMemo<Record<string, SelectionVisualState>>(() => {
    const selectedDescendantFileCountByPrefix: Record<string, number> = {};
    for (const meta of Object.values(selectedState)) {
      if (meta.isFolder) continue;
      const pathSegments = meta.path.split("/").filter(Boolean);
      if (pathSegments.length <= 1) continue;
      let prefix = "";
      for (let index = 0; index < pathSegments.length - 1; index++) {
        prefix = prefix ? `${prefix}/${pathSegments[index]}` : pathSegments[index];
        selectedDescendantFileCountByPrefix[prefix] = (selectedDescendantFileCountByPrefix[prefix] ?? 0) + 1;
      }
    }

    return Object.fromEntries(
      items
        .filter((item) => item.isFolder)
        .map((item) => {
          const normalizedFolderId = item.id.replace(/\/+$/, "");
          const selectedDescendantFileCount = selectedDescendantFileCountByPrefix[normalizedFolderId] ?? 0;
          const hasExplicitFolderSelection = !!selectedState[item.rowKey]?.isFolder;
          const totalFileCount = typeof item.fileCount === "number" ? item.fileCount : null;
          if (selectedDescendantFileCount === 0) {
            // Unknown count (most facets): an explicit folder-checkbox click is the only signal available.
            // Known count (Raw): only a CONFIRMED-empty folder can show "all" with zero selected descendants.
            if (totalFileCount === null) return [item.rowKey, hasExplicitFolderSelection ? "all" : "none"];
            return [item.rowKey, hasExplicitFolderSelection && totalFileCount === 0 ? "all" : "none"];
          }
          if (totalFileCount !== null && totalFileCount >= 0) return [item.rowKey, selectedDescendantFileCount >= totalFileCount ? "all" : "partial"];
          return [item.rowKey, hasExplicitFolderSelection ? "all" : "partial"];
        }),
    );
  }, [items, selectedState]);

  const getItemSelectionState = React.useCallback(
    (item: { rowKey: string; isFolder: boolean }): SelectionVisualState => {
      if (item.isFolder) return folderSelectionStates[item.rowKey] ?? "none";
      return Object.hasOwn(selectedState, item.rowKey) ? "all" : "none";
    },
    [folderSelectionStates, selectedState],
  );

  const selectedFiles = React.useMemo(
    () =>
      Object.values(selectedState)
        .filter((meta) => !meta.isFolder && (!isItemVisibleInScope || isItemVisibleInScope(meta.path, false)))
        .map((meta) => ({ path: meta.path, sizeInBytes: meta.sizeInBytes, sourcePath: meta.sourcePath ?? null })),
    [isItemVisibleInScope, selectedState],
  );

  useEffect(() => {
    onSelectionChange(selectedFiles);
  }, [selectedFiles]);

  const fetchAllFilesRecursively = React.useCallback(
    async (folderPath: string, sourcePath: string | null): Promise<ExplorerItem[]> => {
      const result: ExplorerItem[] = [];

      const fetchFolder = async (currentPath: string) => {
        const response = await getListWithCache(currentPath, sourcePath);
        const apiItems = response.list ?? [];
        for (const apiItem of apiItems) {
          const item = toItem(apiItem);
          // Scope-gates whether the item is SELECTABLE, but recursion always continues regardless -
          // matches Raw's pre-migration behavior (a scoped-out folder can still contain in-scope children).
          if (!isItemVisibleInScope || isItemVisibleInScope(item.id, item.isFolder)) result.push(item);
          if (item.isFolder) await fetchFolder(`${item.id}/`);
        }
      };

      await fetchFolder(folderPath);
      return result;
    },
    [getListWithCache, isItemVisibleInScope, toItem],
  );

  const updateSelectionForItem = React.useCallback(
    async (newSelectedState: ExplorerSelectedState, selectedItem: ExplorerItem, isChecked: boolean) => {
      if (selectionMode === "single") {
        if (selectedItem.isFolder) return;
        if (isChecked) {
          for (const key of Object.keys(newSelectedState)) delete newSelectedState[key];
          newSelectedState[selectedItem.rowKey] = { path: selectedItem.id, sizeInBytes: selectedItem.sizeInBytes, isFolder: false, sourcePath: selectedItem.sourcePath ?? null };
        } else {
          delete newSelectedState[selectedItem.rowKey];
        }
        return;
      }

      if (selectedItem.isFolder) {
        const normalizedFolderPath = `${selectedItem.id.replace(/\/+$/, "")}/`;
        const sourcePath = selectedItem.sourcePath ?? breadcrumbItems.at(-1)?.sourcePath ?? null;
        const subItems = await fetchAllFilesRecursively(normalizedFolderPath, sourcePath);
        if (isChecked) {
          for (const file of subItems) newSelectedState[file.rowKey] = { path: file.id, sizeInBytes: file.sizeInBytes, isFolder: file.isFolder, sourcePath: file.sourcePath };
          newSelectedState[selectedItem.rowKey] = { path: selectedItem.id, sizeInBytes: 0, isFolder: true, sourcePath };
        } else {
          for (const file of subItems) delete newSelectedState[file.rowKey];
          delete newSelectedState[selectedItem.rowKey];
        }
      } else if (isChecked) {
        newSelectedState[selectedItem.rowKey] = {
          path: selectedItem.id,
          sizeInBytes: selectedItem.sizeInBytes,
          isFolder: false,
          sourcePath: selectedItem.sourcePath ?? breadcrumbItems.at(-1)?.sourcePath ?? null,
        };
      } else {
        delete newSelectedState[selectedItem.rowKey];
      }
    },
    [breadcrumbItems, fetchAllFilesRecursively, selectionMode],
  );

  const toggleSelection = React.useCallback(
    async (selectedItem: ExplorerItem, isChecked: boolean, visibleItems: ExplorerItem[], event?: ShiftSelectionEvent) => {
      if (preventDeselection && !isChecked) return;
      const revision = selectionRevisionReference.current;
      const anchorId = selectionAnchorIdReference.current;
      const isShiftRangeSelection = selectionMode === "multi" && Boolean(anchorId && event && hasShiftKey(event));
      const newSelectedState: ExplorerSelectedState = { ...selectedState };

      if (isShiftRangeSelection) {
        const anchorIndex = visibleItems.findIndex((item) => item.rowKey === anchorId);
        const selectedIndex = visibleItems.findIndex((item) => item.rowKey === selectedItem.rowKey);
        if (anchorIndex !== -1 && selectedIndex !== -1) {
          const [startIndex, endIndex] = anchorIndex < selectedIndex ? [anchorIndex, selectedIndex] : [selectedIndex, anchorIndex];
          const rangeItems = visibleItems.slice(startIndex, endIndex + 1);
          for (const item of rangeItems) await updateSelectionForItem(newSelectedState, item, isChecked);
          if (revision !== selectionRevisionReference.current) return;
          setSelectedState(newSelectedState);
          selectionAnchorIdReference.current = selectedItem.rowKey;
          return;
        }
      }

      await updateSelectionForItem(newSelectedState, selectedItem, isChecked);
      if (revision !== selectionRevisionReference.current) return;
      setSelectedState(newSelectedState);
      selectionAnchorIdReference.current = selectedItem.rowKey;
    },
    [preventDeselection, selectedState, selectionMode, setSelectedState, updateSelectionForItem],
  );

  const toggleVisibleSelection = React.useCallback(
    async (isChecked: boolean, visibleItems: ExplorerItem[]) => {
      if (preventDeselection && !isChecked) return;
      const revision = selectionRevisionReference.current;
      const updatedSelectedState: ExplorerSelectedState = { ...selectedState };
      for (const item of visibleItems) await updateSelectionForItem(updatedSelectedState, item, isChecked);
      if (revision !== selectionRevisionReference.current) return;
      setSelectedState(updatedSelectedState);
    },
    [preventDeselection, selectedState, setSelectedState, updateSelectionForItem],
  );

  const resetSelection = React.useCallback(() => {
    if (preventDeselection) return;
    selectionRevisionReference.current++;
    selectionAnchorIdReference.current = null;
    setSelectedState({});
  }, [preventDeselection, setSelectedState]);

  const navigateToFolder = React.useCallback(
    async (folderId: string, folderName: string, sourcePath: string | null, loadingKey: string) => {
      setLoadingFolderId(loadingKey);
      setMetaState({ ...metaState, skip: 0, filter: undefined });
      const folderPath = folderId.endsWith("/") ? folderId : `${folderId}/`;
      await fetchData(folderPath, sourcePath);
      const newItem = { id: folderPath, text: folderName, disabled: false, sourcePath };
      setBreadcrumbItems(breadcrumbItems.some((item) => item.id === newItem.id) ? breadcrumbItems : [...breadcrumbItems, newItem]);
    },
    [breadcrumbItems, fetchData, metaState, setBreadcrumbItems, setMetaState],
  );

  const handleFolderClick = React.useCallback(
    async (item: ExplorerItem) => {
      if (!item.isFolder || loadingFolderId === item.rowKey) return;
      const sourcePath = item.sourcePath ?? breadcrumbItems.at(-1)?.sourcePath ?? null;
      await navigateToFolder(item.id, item.fileName, sourcePath, item.rowKey);
    },
    [breadcrumbItems, loadingFolderId, navigateToFolder],
  );

  const handleBreadcrumbClick = React.useCallback(
    async (crumbId: string) => {
      const clickedItemIndex = breadcrumbItems.findIndex((item) => item.id === crumbId);
      const newBreadcrumbs = breadcrumbItems.slice(0, clickedItemIndex + 1);
      const path = crumbId === "home" ? "" : crumbId;
      const sourcePath = newBreadcrumbs.at(-1)?.sourcePath ?? null;
      setMetaState({ ...metaState, skip: 0, filter: undefined });
      await fetchData(path, sourcePath);
      setBreadcrumbItems(newBreadcrumbs);
    },
    [breadcrumbItems, fetchData, metaState, setBreadcrumbItems, setMetaState],
  );

  const currentFolder = breadcrumbItems.at(-1)?.id === "home" ? "" : (breadcrumbItems.at(-1)?.id ?? "");
  const currentSourcePath = breadcrumbItems.at(-1)?.sourcePath ?? null;

  return {
    loading,
    loadingFolderId,
    getItemSelectionState,
    toggleSelection,
    toggleVisibleSelection,
    resetSelection,
    handleFolderClick,
    handleBreadcrumbClick,
    fetchData,
    clearCache,
    currentFolder,
    currentSourcePath,
  };
}
